import React, { useEffect, useState, useMemo, useRef } from "react";
import { Helmet } from "react-helmet";
import moment from "moment";
import { debounce } from "lodash";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { BlankPreview } from "../../components/BlankPreview";
import nopreview from "../../assets/images/others/no-preview.png";
import { useGetScreenings } from "../../api/screening";

const RISK_COLORS = {
  LOW: { bg: "#DAF4E1", color: "#348530", border: "#34853012" },
  MODERATE: { bg: "#FFF3CD", color: "#E4841A", border: "#E4841A12" },
  HIGH: { bg: "#FFEBE8", color: "#D32F2F", border: "#D32F2F12" },
};

const ECG_COLORS = {
  REQUIRED: { bg: "#FFEBE8", color: "#D32F2F", border: "#D32F2F12" },
  NOT_REQUIRED: { bg: "#DAF4E1", color: "#348530", border: "#34853012" },
};

function RiskChip({ level }) {
  const style = RISK_COLORS[level] || RISK_COLORS.LOW;
  return (
    <Chip
      label={level}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: "12px",
        borderRadius: "6px",
        border: `1px solid ${style.border}`,
        color: style.color,
        backgroundColor: style.bg,
        fontFamily: "Figtree",
      }}
    />
  );
}

function EcgChip({ value }) {
  const style = ECG_COLORS[value] || ECG_COLORS.NOT_REQUIRED;
  const label = value === "REQUIRED" ? "Required" : "Not Required";
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: "12px",
        borderRadius: "6px",
        border: `1px solid ${style.border}`,
        color: style.color,
        backgroundColor: style.bg,
        fontFamily: "Figtree",
      }}
    />
  );
}

const TABLE_HEADERS = [
  "DATE",
  "NAME",
  "WHATSAPP",
  "REG NO.",
  "AGE",
  "SPO2",
  "BP",
  "SUGAR",
  "RISK SCORE",
  "RISK LEVEL",
  "ECG",
];

function Screening() {
  const [page, setPage] = useState(1);
  const limit = 20;
  const [args, setArgs] = useState({ page: 1, limit });
  const [hasMore, setHasMore] = useState(true);
  const listRef = useRef(null);

  const [searchText, setSearchText] = useState("");
  const [riskLevel, setRiskLevel] = useState("");

  const { screenings, isLoading, pagination } = useGetScreenings(args);
  const [screeningData, setScreeningData] = useState(() =>
    Array.isArray(screenings) ? screenings : [],
  );

  useEffect(() => {
    if (!screenings) return;
    setScreeningData((prev) => {
      if (page === 1) return screenings;
      const existingIds = new Set(prev.map((s) => s._id));
      return [...prev, ...screenings.filter((s) => !existingIds.has(s._id))];
    });
    if (pagination?.totalPages) {
      setHasMore(page < pagination.totalPages);
    } else {
      setHasMore(screenings.length === limit);
    }
  }, [screenings, page]);

  const debouncedSearch = useMemo(
    () =>
      debounce((val) => {
        setPage(1);
        setScreeningData([]);
        setArgs((prev) => ({ ...prev, page: 1, search: val }));
      }, 400),
    []
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    debouncedSearch(val);
  };

  const handleRiskLevelChange = (e) => {
    const val = e.target.value;
    setRiskLevel(val);
    setPage(1);
    setScreeningData([]);
    setArgs((prev) => ({ ...prev, page: 1, riskLevel: val }));
  };

  const handleScroll = (e) => {
    const target = e.currentTarget;
    const reachedBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - 120;
    if (reachedBottom && !isLoading && hasMore) {
      const next = page + 1;
      setPage(next);
      setArgs((prev) => ({ ...prev, page: next }));
    }
  };

  const skeletonCount = Math.ceil(window.innerHeight / 60);

  return (
    <>
      <Helmet>
        <title>Spandan - Screening</title>
      </Helmet>

      <Box className="main-wrappers">
        <Box
          className="page-wrapper"
          sx={{ pb: "10px", fontFamily: "Figtree", height: "100vh" }}
        >
          <Box className="content">
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: "20px", fontFamily: "Figtree", mb: 2 }}
            >
              Screening
            </Typography>

            {/* Filters */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mt: 2,
                flexWrap: { xs: "wrap", md: "nowrap" },
                alignItems: "center",
              }}
            >
              <TextField
                variant="outlined"
                placeholder="Search by name or phone number"
                value={searchText}
                onChange={handleSearchChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "var(--app-text-muted)" }} />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: "8px",
                    border: "1px solid var(--app-border)",
                    backgroundColor: "var(--app-surface)",
                    height: "45px",
                    fontSize: "14px",
                    fontFamily: "Figtree",
                    "& fieldset": { border: "none" },
                  },
                }}
              />

              <FormControl sx={{ minWidth: 180, flexShrink: 0 }}>
                <Select
                  value={riskLevel}
                  onChange={handleRiskLevelChange}
                  displayEmpty
                  sx={{
                    height: "45px",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontFamily: "Figtree",
                    backgroundColor: "var(--app-surface)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "var(--app-border)",
                    },
                  }}
                >
                  <MenuItem value="">All Risk Levels</MenuItem>
                  <MenuItem value="LOW">Low</MenuItem>
                  <MenuItem value="MODERATE">Moderate</MenuItem>
                  <MenuItem value="HIGH">High</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Table */}
            {isLoading && screeningData.length === 0 ? (
              <Box sx={{ mt: 3 }}>
                {Array.from({ length: skeletonCount }).map((_, i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    height={50}
                    animation="wave"
                    sx={{ mb: 2 }}
                  />
                ))}
              </Box>
            ) : screeningData.length > 0 ? (
              <TableContainer className="app-table-card"
                component={Paper}
                ref={listRef}
                onScroll={handleScroll}
                sx={{
                  maxHeight: "70vh",
                  overflowY: "auto",
                  fontFamily: "Figtree",
                  mt: 3,
                }}
              >
                <Table
                  stickyHeader
                  sx={{
                    borderCollapse: "separate",
                    borderSpacing: "0 10px",
                    "& th": {
                      borderBottom: "none",
                      fontWeight: 800,
                      fontSize: "15px",
                      color: "#A3A3A3",
                      py: 1.5,
                      backgroundColor: "var(--app-surface-2)",
                      fontFamily: "Figtree",
                    },
                    "& td": {
                      borderBottom: "none",
                      fontSize: "15px",
                      py: 1.5,
                      px: 2,
                      color: "var(--app-text)",
                      fontFamily: "Figtree",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {TABLE_HEADERS.map((h) => (
                        <TableCell key={h}>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 800,
                              fontFamily: "Figtree",
                              color: "#A3A3A3",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {screeningData.map((item, idx) => (
                      <TableRow
                        key={item._id || idx}
                        sx={{
                          backgroundColor: "var(--app-surface)",
                          borderRadius: "8px",
                          "& > *:first-of-type": {
                            borderTopLeftRadius: "8px",
                            borderBottomLeftRadius: "8px",
                          },
                          "& > *:last-of-type": {
                            borderTopRightRadius: "8px",
                            borderBottomRightRadius: "8px",
                          },
                        }}
                      >
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Typography variant="body2" sx={{ fontSize: "14px" }}>
                            {moment(item.createdAt).format("DD/MM/YYYY")}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "13px" }}
                          >
                            {moment(item.createdAt).format("hh:mm a")}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                          {item.name || "-"}
                        </TableCell>

                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {item.whatsapp_number
                            ? item.whatsapp_number.replace(/^91/, "+91 ")
                            : "-"}
                        </TableCell>

                        <TableCell>{item.yatra_reg_no || "-"}</TableCell>

                        <TableCell>{item.age ?? "-"}</TableCell>

                        <TableCell>{item.spo2 || "-"}</TableCell>

                        <TableCell>{item.bp || "-"}</TableCell>

                        <TableCell>
                          {item.sugar === true ? (
                            <Chip
                              label="Yes"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: "12px",
                                borderRadius: "6px",
                                border: "1px solid #D32F2F12",
                                color: "#D32F2F",
                                backgroundColor: "#FFEBE8",
                              }}
                            />
                          ) : item.sugar === false ? (
                            <Chip
                              label="No"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: "12px",
                                borderRadius: "6px",
                                border: "1px solid #34853012",
                                color: "#348530",
                                backgroundColor: "#DAF4E1",
                              }}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>

                        <TableCell sx={{ fontWeight: 700 }}>
                          {item.risk_score ?? "-"}
                          <Typography
                            component="span"
                            sx={{ color: "#A3A3A3", fontSize: "12px" }}
                          >
                            /100
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <RiskChip level={item.risk_level} />
                        </TableCell>

                        <TableCell>
                          <EcgChip value={item.ecg_recommendation} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {isLoading && screeningData.length > 0 ? (
                  <Box textAlign="center" py={2}>
                    <CircularProgress size={20} />
                    <Typography variant="caption" ml={1}>
                      Loading more...
                    </Typography>
                  </Box>
                ) : !hasMore && screeningData.length > limit ? (
                  <Box textAlign="center" py={2}>
                    <Typography variant="caption" color="text.secondary">
                      No more records
                    </Typography>
                  </Box>
                ) : null}
              </TableContainer>
            ) : (
              <Box sx={{ mt: 3 }}>
                <BlankPreview
                  icon={nopreview}
                  title="No screenings found"
                  subtitle="Screenings submitted via the screening form will appear here."
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default Screening;
